import { format } from 'date-fns';
import discountsStrings from '../strings';
import type {
  DiscountItem,
  DiscountValueType,
  OfferLineDraft,
} from '../types/discount.types';
import { DISCOUNT_TYPE_API, OFFER_PROVIDER_TYPE, DiscountActions, discountStockFilter, DiscountLifecycleStatus } from '../constants';


const STOCK_FILTER_MAP: Record<number, string> = {
  [discountStockFilter.All]: 'all',
  [discountStockFilter.InStock]: 'inStock',
  [discountStockFilter.OutOfStock]: 'outOfStock',
};

interface BuildListQueryParamsArgs {
  statusFilter: number;
  stockFilter: number;
  itemTypeFilter: number;
  pageNumber: number;
  pageSize: number;
  startDate?: string;
  endDate?: string;
}

export function buildListQueryParams({
  statusFilter,
  stockFilter,
  itemTypeFilter,
  pageNumber,
  pageSize,
  startDate,
  endDate,
}: BuildListQueryParamsArgs) {
  const params: Record<string, unknown> = {
    pageNumber,
    pageSize,
  };

  if (statusFilter) {
    params.status = statusFilter;
  }
  if (stockFilter) {
    params.stock = STOCK_FILTER_MAP[stockFilter];
  }
  if (itemTypeFilter) {
    params.productType = itemTypeFilter;
  }
  if (startDate) {
    params.StartDate = startDate;
  }
  if (endDate) {
    params.EndDate = endDate;
  }

  return params;
}

export function formatDiscountDateLabel(discount: DiscountItem) {
  if (!discount?.endDate) return '';
  const endDate = new Date(discount.endDate);
  const endLabel = format(endDate, 'dd MMM');

  if (discount.status === DiscountLifecycleStatus.Completed) {
    return `${discountsStrings.endedOnLabel} ${format(endDate, 'dd MMM yyyy')}`;
  }

  if (discount.startDate) {
    const startLabel = format(new Date(discount.startDate), 'dd MMM');
    return `From ${startLabel} to ${endLabel}`;
  }

  return `${discountsStrings.endsInLabel} ${endLabel}`;
}

export function getDiscountedPrice(
  originalPrice: number,
  discountValue: number,
  discountType: DiscountValueType = 'percent',
) {
  const original = Number(originalPrice) || 0;
  const value = Number(discountValue) || 0;
  if (discountType === 'amount') {
    return Math.max(0, Number((original - value).toFixed(2)));
  }
  return Number((original * (1 - value / 100)).toFixed(2));
}

export function toInputDate(value?: string | null) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

export function formatDisplayDate(value?: string | null) {
  if (!value) return '';
  return format(new Date(value), 'dd MMM yyyy');
}

export function hasMorePages(itemCount: number, pageSize: number) {
  return itemCount >= pageSize;
}

export function productSelectionKey(productKey: string, productShapeId: number) {
  return `${productKey}-${productShapeId}`;
}

export function toApiDiscountType(type: DiscountValueType): number {
  return type === 'amount' ? DISCOUNT_TYPE_API.Amount : DISCOUNT_TYPE_API.Percent;
}

export function toApiDiscountTypeString(type: DiscountValueType): string {
  return String(toApiDiscountType(type));
}

interface BuildCreateOfferPayloadArgs {
  offerLines: OfferLineDraft[];
  startDate: string;
  endDate: string;
  providerKey: string;
}

export function buildCreateOfferPayload({
  offerLines,
  startDate,
  endDate,
  providerKey,
}: BuildCreateOfferPayloadArgs) {
  const startDateIso = startDate ? new Date(startDate).toISOString() : '';
  const endDateIso = endDate ? new Date(endDate).toISOString() : '';

  return {
    providerType: OFFER_PROVIDER_TYPE,
    providerKey,
    items: offerLines.map(line => ({
      itemKey: line.productKey,
      discountType: toApiDiscountTypeString(line.discountType),
      discountValue: Number(line.discountValue),
      startDate: startDateIso,
      endDate: endDateIso,
    })),
  };
}

interface ApiErrorPayload {
  response?: { data?: unknown };
  data?: unknown;
  message?: string;
}

export function extractApiErrorMessage(error: unknown): string {
  const err = error as ApiErrorPayload;
  const data = (err?.response?.data ?? err?.data ?? error) as Record<string, unknown> | string | null;

  if (data && typeof data === 'object' && data.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors as Record<string, unknown>).flat();
    const text = messages.filter(message => typeof message === 'string').join(' ');
    if (text) return text;
  }

  if (data && typeof data === 'object' && typeof data.title === 'string') {
    return data.title;
  }

  if (typeof data === 'string' && data) return data;
  if (err?.message) return err.message;

  return discountsStrings.actionError;
}
